import type { StaffSegment } from '@unified-lk/api-contracts'

const MAX_SEGMENTS = 3

interface Props {
  segments: StaffSegment[]
  selected: number[]
  onChange: (ids: number[]) => void
  loading?: boolean
  error?: string | null
}

export function SegmentSelector({ segments, selected, onChange, loading, error }: Props) {
  function toggle(id: number) {
    if (selected.includes(id)) {
      onChange(selected.filter((s) => s !== id))
    } else if (selected.length < MAX_SEGMENTS) {
      onChange([...selected, id])
    }
  }

  if (loading) {
    return (
      <div className="flex items-center gap-2 rounded-2xl bg-slate-50 p-4 text-sm text-slate-500">
        <span className="inline-block h-4 w-4 animate-spin rounded-full border-2 border-sky-500 border-t-transparent" />
        Загрузка сегментов...
      </div>
    )
  }

  if (error) {
    return (
      <p className="rounded-2xl bg-amber-50 px-4 py-3 text-sm text-amber-700">
        ⚠️ {error}
      </p>
    )
  }

  if (segments.length === 0) {
    return (
      <p className="rounded-2xl bg-slate-50 px-4 py-3 text-sm text-slate-500">
        Сегменты не найдены в Segments API.
      </p>
    )
  }

  const remaining = MAX_SEGMENTS - selected.length

  return (
    <div>
      <div className="mb-2 flex items-center justify-between gap-2">
        <span className="text-xs text-slate-500">Выбрано {selected.length} из {MAX_SEGMENTS}</span>
        {remaining === 0 && (
          <span className="rounded-full bg-sky-100 px-2.5 py-0.5 text-[11px] font-semibold text-sky-700">
            Максимум выбрано
          </span>
        )}
      </div>
      <div className="max-h-56 overflow-y-auto rounded-2xl border border-slate-200 bg-white">
        {segments.map((seg) => {
          const id = seg.id
          const isChecked = selected.includes(id)
          const isDisabled = !isChecked && remaining === 0
          const label = seg.name ?? seg.title ?? `Сегмент #${id}`
          const isDynamic = seg.type === 'dynamic'

          return (
            <label
              key={id}
              className={`flex cursor-pointer items-center gap-3 border-b border-slate-100 px-4 py-3 last:border-none transition-colors ${
                isChecked ? 'bg-sky-50' : isDisabled ? 'opacity-50 cursor-not-allowed' : 'hover:bg-slate-50'
              }`}
            >
              <input
                type="checkbox"
                checked={isChecked}
                disabled={isDisabled}
                onChange={() => toggle(id)}
                className="h-4 w-4 rounded border-slate-300 text-sky-600 accent-sky-600"
              />
              <span className="flex-1 text-sm text-slate-800">{label}</span>
              <span className={`rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide ${
                isDynamic ? 'bg-purple-100 text-purple-700' : 'bg-slate-100 text-slate-600'
              }`}>
                {isDynamic ? 'Дин' : 'Стат'}
              </span>
              {seg.patients_count !== undefined && (
                <span className="text-[11px] text-slate-400">{seg.patients_count} пац.</span>
              )}
            </label>
          )
        })}
      </div>
    </div>
  )
}
